// Hook de presencia para admins: muestra quien esta conectado al panel y en
// que pantalla esta navegando. Usa Supabase Realtime presence channel.
//
// Uso:
//   const admins = useAdminPresence()
//   admins => [{ user_id, nombre, email, path, last_seen }, ...]
//
// Se actualiza automaticamente cuando alguien entra/sale del panel
// o cambia de pagina (via useLocation).

import { useEffect, useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { supabase } from './supabase'
import { useAuth } from './AuthContext'

const CHANNEL_NAME = 'admins-online'

export function useAdminPresence() {
  const { user, profile, role } = useAuth()
  const location = useLocation()
  const [admins, setAdmins] = useState([])
  const channelRef = useRef(null)

  // Suscripcion al canal (una sola vez por sesion de admin)
  useEffect(() => {
    if (!user?.id || role !== 'admin') return

    const channel = supabase.channel(CHANNEL_NAME, {
      config: { presence: { key: user.id } },
    })

    channel.on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState()
      // state = { 'uuid1': [{...meta1}], 'uuid2': [{...meta2}], ... }
      // Si un mismo user tiene varias tabs, hay multiples metas. Tomamos la mas reciente.
      const flat = []
      for (const [key, metas] of Object.entries(state)) {
        if (!metas?.length) continue
        const latest = metas.reduce((a, b) =>
          new Date(b.last_seen || 0) > new Date(a.last_seen || 0) ? b : a
        )
        flat.push({ user_id: key, ...latest })
      }
      setAdmins(flat)
    })

    channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await channel.track({
          user_id: user.id,
          nombre: profile?.nombre || user.email?.split('@')[0] || 'Admin',
          email: user.email,
          path: location.pathname,
          last_seen: new Date().toISOString(),
        })
      }
    })

    channelRef.current = channel

    return () => {
      supabase.removeChannel(channel)
      channelRef.current = null
    }
    // Solo re-suscribir cuando cambia el user o el rol
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, role, profile?.nombre])

  // Re-track cuando cambia el path (sin reconectar canal)
  useEffect(() => {
    if (!channelRef.current || !user?.id || role !== 'admin') return
    channelRef.current.track({
      user_id: user.id,
      nombre: profile?.nombre || user.email?.split('@')[0] || 'Admin',
      email: user.email,
      path: location.pathname,
      last_seen: new Date().toISOString(),
    })
  }, [location.pathname, user?.id, role, profile?.nombre, user?.email])

  return admins
}

/** Resuelve un path a un label legible usando el MENU del AdminLayout.
 *  Ej: '/admin/clientes' -> 'Suscriptores' */
export function pathToLabel(path, menu) {
  if (!path) return ''
  // Buscar match exacto primero
  for (const item of menu) {
    if (item.type === 'link' && item.to === path) return item.label
    if (item.type === 'group') {
      for (const sub of item.items || []) {
        if (sub.to === path) return sub.label
      }
    }
  }
  // Buscar match por prefijo (p.ej. /admin/clientes/123 -> Suscriptores)
  let bestMatch = null
  for (const item of menu) {
    if (item.type === 'link' && path.startsWith(item.to) && item.to !== '/admin') {
      if (!bestMatch || item.to.length > bestMatch.to.length) bestMatch = item
    }
    if (item.type === 'group') {
      for (const sub of item.items || []) {
        if (path.startsWith(sub.to)) {
          if (!bestMatch || sub.to.length > bestMatch.to.length) bestMatch = sub
        }
      }
    }
  }
  if (bestMatch) return bestMatch.label
  // Fallback: el path crudo sin /admin/
  return path.replace(/^\/admin\/?/, '') || 'Dashboard'
}
